import styled from "styled-components/macro";
import type { Process } from "types/types";
import {
  useRollupDisplayForProcess,
  useSingleProcessFullRangeDataDump,
} from "utils/customHooks/genericHooks";
import {
  LOSS_CATEGORIES_STRINGS,
  PROFIT_CATEGORIES_STRINGS,
  PNL_ORDER,
  PNL_COLORS,
} from "constants/constants";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { getExpandedTileContentHeights } from "./ExpandedTileModules";
import { TILE_DIVIDER_HEIGHT } from "pages/useTileSize";
import { useShouldUseDataDumpsForTileButterfly } from "utils/customHooks/whichServerHooks";
import { useMapDataDumpToRollup } from "components/Dashboard/TrendChart/TrendChartContent/trendChartContentUtils";
import { HtmlTooltip } from "components/Dashboard/GroupMetrics/HtmlTooltip";
import OverallBarstackTooltipContent from "components/Dashboard/OverallBarstack/OverallBarstackTooltipContent";

const sortPnlOrder = (a, b) => PNL_ORDER[a[0]] - PNL_ORDER[b[0]];

const TileButterfly = ({ process }: { process: Process }) => {
  const shouldUseDataDump = useShouldUseDataDumpsForTileButterfly();
  const dataDumpForProcess = useSingleProcessFullRangeDataDump(process.id);
  const aggregateForProcess = useRollupDisplayForProcess(process.id);
  const mapDataDumpToRollup = useMapDataDumpToRollup();
  const aggregateToUse =
    shouldUseDataDump && dataDumpForProcess
      ? mapDataDumpToRollup(dataDumpForProcess)
      : aggregateForProcess;

  const losses = aggregateToUse
    ? (
        Object.entries(aggregateToUse).filter(([pnlCatOrOther, timeMs]) =>
          LOSS_CATEGORIES_STRINGS.includes(pnlCatOrOther)
        ) as [string, number][]
      ).sort(sortPnlOrder)
    : [];

  const profits = aggregateToUse
    ? (
        Object.entries(aggregateToUse).filter(([pnlCatOrOther, timeMs]) =>
          PROFIT_CATEGORIES_STRINGS.includes(pnlCatOrOther)
        ) as [string, number][]
      ).sort(sortPnlOrder)
    : [];

  const { isLargeTiles, tileScaleMultiplier } = useSettings();
  return (
    <HtmlTooltip
      title={
        <OverallBarstackTooltipContent
          rollupDisplay={aggregateToUse}
          style={{
            pointerEvents: "none",
          }}
        />
      }
      openOnMouseover={true}
      leaveDelay={0}
      enterDelay={0}
      stickyOpen={false}
    >
      <TileButterflyStyles
        className="tileButterfly"
        {...{
          $isLargeTiles: isLargeTiles,
          $tileScaleMultiplier: tileScaleMultiplier,
        }}
      >
        {profits.map(([pnlCat, timeMs]) => (
          <div
            key={pnlCat}
            className="pnlCategory profitCategory"
            style={{ flexGrow: timeMs, background: PNL_COLORS[pnlCat] }}
          />
        ))}
        <div className="gaugeSpacer">
          <div className="gaugeMarker" />
        </div>
        {losses.map(([pnlCat, timeMs]) => (
          <div
            key={pnlCat}
            className="pnlCategory lossCategory"
            style={{ flexGrow: timeMs, background: PNL_COLORS[pnlCat] }}
          />
        ))}
      </TileButterflyStyles>
    </HtmlTooltip>
  );
};

export default TileButterfly;

const TileButterflyStyles = styled.div<{
  $isLargeTiles: boolean;
  $tileScaleMultiplier: number;
}>`
  padding-top: ${props =>
    2 * TILE_DIVIDER_HEIGHT * props.$tileScaleMultiplier}px;
  display: flex;
  height: ${props =>
    getExpandedTileContentHeights().tileButterfly[
      props.$isLargeTiles ? "lg" : "sm"
    ]}px;
  .gaugeSpacer {
    position: relative;
    width: ${props =>
      2 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
    .gaugeMarker {
      width: ${props =>
        6 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
      height: ${props =>
        9 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
      background: black;
      position: absolute;
      border-radius: ${props =>
        1 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
      left: ${props =>
        -2 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
      bottom: ${props =>
        -3 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
    }
  }
  .pnlCategory:first-child {
    border-bottom-left-radius: ${props =>
      1 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
    border-top-left-radius: ${props =>
      1 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
  }
  .pnlCategory:last-child {
    border-bottom-right-radius: ${props =>
      1 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
    border-top-right-radius: ${props =>
      1 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
  }
`;
